import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Permissions from '../../../../shared/auth/constants/permissions';
import { useFlutter } from '../../../components/FlutterProvider';
import { usePublicFn } from '../../../utilities/usePublic';

export type MenuItem = {
    icon: string;
    text: string;
    to: string;
    allowedPermissions?: Permissions[];
    displayForBiometricSetting?: boolean;
};

export const useMenuItems = (): MenuItem[] => {
    const publicFn = usePublicFn();
    const { t } = useTranslation(['homePage']);
    const { capabilities } = useFlutter();

    const isDeviceCompatibleWithBiometric = capabilities.hasFingerPrint || capabilities.hasFaceID;

    return useMemo(
        () => [
            {
                icon: publicFn('assets/menuIcons/dashboard-24px.svg'),
                text: t('homePage:menuBar.dashboard'),
                to: '/',
                allowedPermissions: [Permissions.DASHBOARD],
            },
            {
                icon: publicFn('assets/menuIcons/account_circle-24px.svg'),
                text: t('homePage:menuBar.userDatabase'),
                to: '/users',
                allowedPermissions: [Permissions.USER_ACCOUNT_LIST],
            },
            {
                icon: publicFn('assets/menuIcons/gpp_good-24px.svg'),
                text: t('homePage:menuBar.approvals'),
                to: '/approvals',
                allowedPermissions: [Permissions.USER_ACCOUNT_APPROVAL],
            },
            {
                icon: publicFn('assets/menuIcons/Group_6029.svg'),
                text: t('homePage:menuBar.salesTransactionRecords'),
                to: '/salesTransactions',
                allowedPermissions: [Permissions.SALES_TRANSACTION_VIEW_ALL_TRANSACTIONS],
            },

            {
                icon: publicFn('assets/menuIcons/Group_6028.svg'),
                text: t('homePage:menuBar.myBids'),
                to: '/salesTransactions',
                allowedPermissions: [Permissions.SALES_TRANSACTION_VIEW_MY_TRANSACTIONS],
            },

            {
                icon: publicFn('assets/menuIcons/Group_6028.svg'),
                text: t('homePage:menuBar.myBids'),
                to: '/myBids',
                allowedPermissions: [Permissions.SALES_TRANSACTION_VIEW_MY_OFFERS],
            },
            {
                icon: publicFn('assets/menuIcons/Group_6028.svg'),
                text: t('homePage:menuBar.allBiddingSessions'),
                to: '/allBiddingSessions',
                allowedPermissions: [Permissions.SALES_TRANSACTION_VIEW_CURRENT_TRANSACTIONS],
            },
            {
                icon: publicFn('assets/menuIcons/circle_notifications-24px.svg'),
                text: t('homePage:menuBar.notificationsLog'),
                to: '/notifications',
                allowedPermissions: [Permissions.NOTIFICATION_LOG_LIST],
            },
            {
                icon: publicFn('assets/menuIcons/history.svg'),
                text: t('homePage:menuBar.activityHistory'),
                to: '/activityHistory',
                allowedPermissions: [Permissions.ACTIVITIES_LOG_EXPORT],
            },
            {
                icon: publicFn('assets/menuIcons/settings.svg'),
                text: t('homePage:menuBar.settings'),
                to: '/settings',
                allowedPermissions: [
                    Permissions.SETTINGS_UPDATE_TEMPLATE_USE_CASES,
                    Permissions.SETTINGS_UPDATE_COMPANY_SETTINGS,
                ],
                displayForBiometricSetting: isDeviceCompatibleWithBiometric,
            },
        ],
        [publicFn, t, isDeviceCompatibleWithBiometric]
    );
};

export default useMenuItems;
